import Layout from "@/components/layout"
import { PLUSBAKE_FAVICON_URL } from "@/lib/constants/site"
import { setupGa4WithConsent } from "@/lib/utils/analytics"
import { captureAttributionFromCurrentLocation } from "@/lib/utils/attribution"
import { shouldBypassMedusaRegionLoader } from "@/lib/utils/static-routes"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import {
  HeadContent,
  Scripts,
  createRootRouteWithContext,
  useLocation,
} from "@tanstack/react-router"
import { lazy, useEffect } from "react"
import appCss from "@/styles/app.css?url"

const NotFound = lazy(() => import("@/components/not-found"))
/**
 * Promotes the `media="print"` font stylesheets back to `all` once they load.
 *
 * Inline rather than an `onload=` attribute because the CSP sets
 * `script-src-attr 'none'`, which blocks attribute handlers; inline <script>
 * elements are still allowed by `script-src 'unsafe-inline'`.
 *
 * A sheet can finish loading before this runs, and `load` would never fire
 * again — `link.sheet` is non-null once parsed, so that case is handled up
 * front. Without it the brand fonts would silently never apply on a warm
 * cache, which is exactly when the sheet is fastest.
 *
 * Mutating `media` ahead of hydration does not desync React's head. React 19
 * renders these with `precedence`, which makes them hoisted stylesheet
 * resources deduped by href rather than ordinary hydrated host elements, so
 * the attribute never goes through the hydration reconciler. Checked in
 * Chromium against both a dev and a production build: no hydration warning on
 * a cold or warm load, and `media` stays `all` across client-side navigations
 * and re-renders.
 */
const ASYNC_STYLE_SCRIPT = `
(function () {
  var links = document.querySelectorAll('link[data-async-style]');
  for (var i = 0; i < links.length; i++) {
    (function (link) {
      if (link.sheet) {
        link.media = 'all';
        return;
      }
      link.addEventListener('load', function () {
        link.media = 'all';
      }, { once: true });
    })(links[i]);
  }
})();
`.trim()

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  loader: async ({ context, location }) => {
    const { queryClient } = context

    if (shouldBypassMedusaRegionLoader(location.pathname)) {
      return {}
    }
    
    // Pre-populate regions cache when Medusa is reachable. Static marketing
    // routes should not fail just because store data is temporarily unavailable.
    try {
      const { listRegions } = await import("@/lib/data/regions")

      await queryClient.ensureQueryData({
        queryKey: ["regions"],
        queryFn: () => listRegions({ fields: "id, name, currency_code, *countries" }),
      })
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn("[+Bake Cafe] Failed to prefetch Medusa regions", error)
      }
    }

    // Feature toggles drive the navbar, footer and homepage, so they have to be
    // in the cache before the first render or a hidden link flashes in. The
    // fetcher resolves to the build-time fallback instead of throwing.
    const { siteSettingsQueryOptions } = await import("@/lib/data/site-settings")
    await queryClient.ensureQueryData(siteSettingsQueryOptions)
    
    return {}
  },
  head: () => {
    // GA4 is intentionally NOT loaded here. It is injected only after the
    // visitor grants analytics consent (see setupGa4WithConsent), so nothing
    // non-essential fires on page render.
    return {
      links: [
        // Browsers request /favicon.ico on their own whatever the page declares,
        // and without the file the SPA catch-all answered with a ~95 KB HTML 404
        // page and a console error. Declared first so the small icon is the one
        // used, with the 512 PNG kept for high-DPI surfaces.
        { rel: "icon", href: "/favicon.ico", sizes: "48x48 32x32 16x16" },
        { rel: "icon", href: PLUSBAKE_FAVICON_URL, type: "image/png", sizes: "512x512" },
        { rel: "apple-touch-icon", href: PLUSBAKE_FAVICON_URL, sizes: "512x512" },
        // FY26 brand fonts, applied site-wide via --font-display / --font-sans.
        // Belanosima (headings) via Google Fonts; Brandon Grotesque / Basic Sans /
        // Bebas Neue Pro (body + variations) via the licensed Adobe Typekit kit.
        { rel: "preconnect", href: "https://fonts.googleapis.com" },
        { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
        // The Typekit kit is render-blocking and its CSS @imports p.css from a
        // second host, so both need a warmed connection or the fonts cost two
        // cold TLS handshakes on the critical path.
        { rel: "preconnect", href: "https://use.typekit.net", crossOrigin: "anonymous" },
        { rel: "preconnect", href: "https://p.typekit.net", crossOrigin: "anonymous" },
        // Both font sheets are cross-origin and were blocking the first paint
        // for ~1.5s. `media="print"` makes the browser fetch them without
        // blocking render; ASYNC_STYLE_SCRIPT flips them to `all` on load, so
        // text paints immediately in the fallback face and swaps when the
        // brand fonts arrive. appCss stays blocking — it is same-origin and
        // carries the layout, so letting it race would cause a real FOUC.
        {
          rel: "stylesheet",
          href: "https://fonts.googleapis.com/css2?family=Belanosima:wght@400;600;700&display=swap",
          media: "print",
          "data-async-style": "",
        },
        {
          rel: "stylesheet",
          href: "https://use.typekit.net/aga1tvq.css",
          media: "print",
          "data-async-style": "",
        },
        { rel: "stylesheet", href: appCss },
      ],
      scripts: [{ children: ASYNC_STYLE_SCRIPT }],
      meta: [
        { title: "+Bake Cafe | Asian-Fusion Artisan Bakery & Café — Rancho Cucamonga, CA" },
        { charSet: "UTF-8" },
        {
          name: "viewport",
          content: "width=device-width, initial-scale=1.0",
        },
        {
          name: "description",
          content: "Asian-fusion artisan bakery in Rancho Cucamonga. Salt bread, pandan and ube treats, and specialty lattes. Order online for pickup.",
        },
        {
          name: "theme-color",
          content: "#FFF9F8",
        },
      ],
    }
  },
  notFoundComponent: NotFound,
  component: RootComponent,
})

// Static inline script to shim process.env in the browser for TanStack Start
// client-rpc which references process.env.TSS_SERVER_FN_BASE at runtime.
// Content is a hardcoded string literal — no user input, safe from XSS.
const PROCESS_SHIM_SCRIPT = [
  'if(typeof process==="undefined"){',
  'window.process={env:{TSS_SERVER_FN_BASE:"/_server"}}',
  "}",
].join("")

function RootComponent() {
  const { queryClient } = Route.useRouteContext()
  const location = useLocation()

  // Capture UTM / paid-click params on every navigation. The helper
  // dedupes: nothing to store → no-op; same params → no-op; new paid-click
  // ID → overwrite. Cheap to run, idempotent.
  useEffect(() => {
    captureAttributionFromCurrentLocation()
  }, [location.pathname, location.search])

  useEffect(() => {
    return setupGa4WithConsent(import.meta.env.VITE_GA4_ID as string | undefined)
  }, [])

  return (
    <html lang="en">
      <head>
        <HeadContent />
        {/* Shim process.env for browser — TanStack Start client-rpc needs it */}
        <script
          dangerouslySetInnerHTML={{
            __html: PROCESS_SHIM_SCRIPT,
          }}
        />
      </head>
      <body>
        <QueryClientProvider client={queryClient}>
          <Layout />
        </QueryClientProvider>

        <Scripts />
      </body>
    </html>
  )
}
